import React, { useLayoutEffect, useMemo, useRef } from 'react';

import PropTypes from 'prop-types';

import { TIMES } from '../../../constants';
import { useCalendar, useFilters } from '../../../hooks';
import {
  filterAppointments,
  getAppointmentsByDateTime,
  getWeekDates,
} from '../../../utils';
import HeaderCell from './HeaderCell';
import { TIME_SLOT_HEIGHT } from './constants';
import TimeSlot from './TimeSlot';

const FIRST_VISIBLE_HOUR = 6;
const GRID_STYLES = 'grid grid-cols-[72px_repeat(7,minmax(0,1fr))]';

const Week = ({ currentDate }) => {
  const scrollRef = useRef(null);

  const { appointments } = useCalendar();

  const {
    activeFilters: { creators: creatorIds, tags: tagIds },
  } = useFilters();

  const filteredAppointments = useMemo(
    () => filterAppointments({ appointments, creatorIds, tagIds }),
    [appointments, creatorIds, tagIds]
  );

  const weekDates = useMemo(() => getWeekDates(currentDate), [currentDate]);

  const appointmentsByDateTime = useMemo(
    () =>
      getAppointmentsByDateTime({ appointments: filteredAppointments, dates: weekDates }),
    [filteredAppointments, weekDates]
  );

  // On mount (useLayoutEffect), set the scroll position to the first visible hour (6 AM).
  // Reset the scroll position each time a new week is displayed (i.e. when weekId
  // changes).
  const weekId = weekDates[0].getTime();

  useLayoutEffect(() => {
    scrollRef.current.scrollTop = FIRST_VISIBLE_HOUR * TIME_SLOT_HEIGHT;
  }, [weekId]);

  return (
    <div className="flex flex-col min-h-0">
      <div className={`${GRID_STYLES} border-b`}>
        <div />

        {weekDates.map((date) => (
          <HeaderCell date={date} key={date.toISOString()} />
        ))}
      </div>

      <div className="overflow-y-auto" ref={scrollRef}>
        <div className={GRID_STYLES}>
          {TIMES.map((time) => (
            <TimeSlot
              appointmentsByDate={appointmentsByDateTime[time]}
              key={time}
              time={time}
              weekDates={weekDates}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

Week.propTypes = {
  currentDate: PropTypes.instanceOf(Date).isRequired,
};

export default Week;
